/**
 * CopilotGreeting - proactive, role-aware copilot opening screen.
 *
 * Inspired by Bob (GMC): instead of opening to a blank prompt,
 * the copilot greets the user with context and suggestions.
 *
 * Shows:
 * 1. Personalized greeting with role context
 * 2. "What I can help with" suggestions based on role + vertical
 * 3. Quick-tap prompts that start a conversation
 *
 * This replaces the blank state when no conversation is active.
 */
import {
  Sparkles, Users, FolderOpen, BarChart3, ClipboardList,
  DollarSign, Package, Calendar, FileText, Megaphone,
  AlertCircle,
} from "lucide-react";

// (no local state: the composer moved to the drawer footer)

import { useAuth } from "@/features/auth/model/useAuth";
import { useListCasesQuery } from "@/features/casework/api/casesApi";

import { useGetPreferencesQuery } from "@/features/manifest/api/preferencesApi";
import { useVerticalVocab } from "@/shared/lib/verticals";

interface Props {
  onStartChat: (prompt: string) => void;
}

interface QuickPrompt {
  icon: typeof Sparkles;
  label: string;
  prompt: string;
}

function getQuickPrompts(role: string, verticalId: string, v: ReturnType<typeof useVerticalVocab>): QuickPrompt[] {
  const base: QuickPrompt[] = [];

  // Role-specific prompts
  switch (role) {
    case "OWNER":
    case "ADMIN":
      base.push(
        { icon: BarChart3, label: "How are we doing this month?", prompt: "Give me a summary of how our organization is performing this month - people served, key metrics, anything that needs my attention." },
        { icon: Users, label: "Who needs follow-up?", prompt: "Are there any cases or people that need follow-up this week? Check for overdue tasks and upcoming deadlines." },
        { icon: FileText, label: "Help me draft a report", prompt: "I need to put together a report for our board meeting. Can you pull together our key numbers for the last 90 days?" },
      );
      break;
    case "STAFF":
      base.push(
        { icon: Users, label: `How many ${v.people.plural} this month?`, prompt: `How many ${v.people.plural} have we served this month? Give me a breakdown.` },
        { icon: FolderOpen, label: `My open ${v.cases.plural}`, prompt: `Show me my open ${v.cases.plural} and which ones need attention.` },
        { icon: FileText, label: "Log an interaction", prompt: "I need to log an interaction - a phone call, visit, or meeting. Walk me through it." },
      );
      break;
    case "VOLUNTEER":
      base.push(
        { icon: Calendar, label: "My upcoming shifts", prompt: "What shifts am I signed up for this week?" },
        { icon: ClipboardList, label: "My tracked hours", prompt: "How many hours have I tracked this week?" },
      );
      break;
    case "MENTOR":
      base.push(
        { icon: FolderOpen, label: `My ${v.cases.plural}`, prompt: `Show me my ${v.cases.plural} - who am I matched with and what's their status?` },
        { icon: FileText, label: "Log a session", prompt: "I just met with my mentee. Help me log the session details." },
      );
      break;
    case "BOARD":
    case "BOARD_MEMBER":
      base.push(
        { icon: BarChart3, label: "Organization overview", prompt: "Give me a high-level overview of how the organization is doing - the numbers I should know before the board meeting." },
      );
      break;
    case "ACCOUNTANT":
      base.push(
        { icon: DollarSign, label: "Giving summary", prompt: "Summarize our giving this month - total received, top donors, fund breakdown." },
        { icon: FileText, label: "Draft a thank-you", prompt: "Help me draft a thank-you letter for a recent donor." },
      );
      break;
  }

  // Vertical-specific prompts (+ intake playbooks v4.4.4)
  switch (verticalId) {
    case "food_access":
      base.push(
        { icon: Package, label: "Inventory status", prompt: "What's our current inventory status? Are any categories running low?" },
        { icon: Users, label: "Register a new family", prompt: "I need to register a new family for food assistance. Walk me through it." },
      );
      break;
    case "youth_programs":
      base.push(
        { icon: ClipboardList, label: "Attendance this week", prompt: "How many students attended our programs this week?" },
        { icon: Users, label: "Enroll a new student", prompt: "I need to enroll a new student. Walk me through the intake process step by step." },
      );
      break;
    case "faith_based":
      base.push(
        { icon: DollarSign, label: "Sunday giving", prompt: "How did this Sunday's offering compare to last week?" },
        { icon: Users, label: "Welcome a new member", prompt: "Someone new wants to join our congregation. Walk me through the welcome process." },
      );
      break;
    case "advocacy":
      base.push(
        { icon: Megaphone, label: "Campaign progress", prompt: "How are our active campaigns doing? How many people have we mobilized this month?" },
        { icon: Users, label: "Onboard a coalition contact", prompt: "Someone new wants to join our coalition. Walk me through onboarding them." },
      );
      break;
  }

  return base.slice(0, 5);
}

export function CopilotGreeting({ onStartChat }: Props) {
  const { user } = useAuth();
  const v = useVerticalVocab();
  const { data: prefs } = useGetPreferencesQuery();
  const role = user?.role || "STAFF";
  const firstName = user?.first_name || "there";
  const prompts = getQuickPrompts(role, v.id, v).filter((p) => {
    const hay = `${p.label} ${p.prompt}`.toLowerCase();
    if (prefs?.grant_management_enabled === false && hay.includes("grant")) return false;
    if (prefs?.inventory_enabled === false && hay.includes("inventory")) return false;
    if (prefs?.batch_giving_enabled === false && hay.includes("giving")) return false;
    if (prefs?.campaign_actions_enabled === false && hay.includes("campaign")) return false;
    return true;
  });

  const { data: casesData } = useListCasesQuery(
    { status: "active" } as Record<string, unknown>,
    { skip: role !== "OWNER" && role !== "ADMIN" },
  );
  const openCaseCount = casesData?.results?.length ?? 0;

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-foreground">
          {greeting}, {firstName}
        </h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Ask about your {v.people.plural.toLowerCase()}, {v.cases.plural.toLowerCase()} or
          schedule. I can search your data, draft messages and run workflows.
        </p>
      </div>

      {/* The composer, first and full width. The screen used to end with
          "Or type anything below" and then have nothing below - the one
          action this screen exists for was the one thing it did not offer. */}
      {/* The composer used to sit here, inline, halfway down a scrolling
          column - the one control this screen exists for, and it scrolled
          away. It now lives in the drawer footer, pinned, where the composer
          for an open conversation already was. See CopilotComposer. */}

      {/* One line of context, and only when it asks something of somebody.
          This was a three-up metric tile - the exact "dashboard-first admin
          template" PRODUCT.md names as an anti-reference - reporting "$0
          giving" among other numbers that change nothing here. The
          Dashboard is where numbers live; this is where questions start. */}
      {openCaseCount > 0 && (
        <button
          type="button"
          onClick={() =>
            onStartChat(
              `Which of our ${openCaseCount} open ${v.cases.plural.toLowerCase()} need attention first?`,
            )
          }
          className="flex w-full items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <AlertCircle className="size-4 shrink-0 text-warning" aria-hidden />
          <span>
            {openCaseCount} open {v.cases.plural.toLowerCase()} - ask which need
            attention first
          </span>
        </button>
      )}

      {/* Suggestions as rows, not cards. Bordered cards inside an already
          bordered overlay is a box in a box, and these are prompts to pick,
          not objects to compare. */}
      <div>
        <p className="mb-1 text-sm font-medium text-foreground">
          Things people ask
        </p>
        <ul className="-mx-2 divide-y divide-border">
          {prompts.map((p, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => onStartChat(p.prompt)}
                className="flex w-full items-start gap-3 rounded-md px-2 py-3 text-left transition-colors hover:bg-muted/60 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
              >
                <p.icon
                  className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-foreground">
                    {p.label}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {p.prompt}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
